import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Save,
  CheckCircle2,
  FileCheck,
  RotateCcw,
  Loader2,
  Eye,
  Columns,
  Layers,
  FileText,
  BookOpen,
  Crosshair,
  Calendar,
  CheckSquare,
  ShieldAlert,
  DollarSign,
  Terminal,
  UserCheck,
  History,
  X,
  Clock,
  Lock,
  Unlock,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import usePageTitle from "../../../utils/hooks/usePageTitle";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { getResponseById } from "../../../utils/api/endpoints/questionnaire";
import {
  getSow,
  saveSowDraft,
  submitSowForReview,
  approveSow,
  reopenSow,
  emptySowFields,
  SowFields,
  SowStatus,
  SowBlob,
  SowHistoryEntry
} from "../../../utils/api/endpoints/sow";
import { SowModule } from "../../../components/scoping/SowPdfDocument";
import { CoverMetaEditor } from "./components/editors/CoverMetaEditor";
import { IntroObjectivesEditor } from "./components/editors/IntroObjectivesEditor";
import { TargetsScopeEditor } from "./components/editors/TargetsScopeEditor";
import { MethodologyCoverageEditor } from "./components/editors/MethodologyCoverageEditor";
import { TimelineDeliverablesEditor } from "./components/editors/TimelineDeliverablesEditor";
import { DependenciesAssumptionsEditor } from "./components/editors/DependenciesAssumptionsEditor";
import { DisclaimersExclusionsEditor } from "./components/editors/DisclaimersExclusionsEditor";
import { CommercialTermsEditor } from "./components/editors/CommercialTermsEditor";
import { AppendixARulesOfEngagementEditor } from "./components/editors/AppendixARulesOfEngagementEditor";
import { AcceptanceSignaturesEditor } from "./components/editors/AcceptanceSignaturesEditor";
import { SowPreviewPanel } from "./components/SowPreviewPanel";

type SectionTab =
  | "cover"
  | "intro"
  | "targets"
  | "methodology"
  | "timeline"
  | "dependencies"
  | "disclaimers"
  | "commercial"
  | "appendix"
  | "acceptance";

interface SectionNavItem {
  id: SectionTab;
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
}

const SECTIONS: SectionNavItem[] = [
  { id: "cover", title: "Cover Page", subtitle: "Metadata & document ID", icon: FileText },
  { id: "intro", title: "1 & 2. Intro & Objectives", subtitle: "Personalized scope purpose", icon: BookOpen },
  { id: "targets", title: "3. Scope & Targets", subtitle: "Authorised assets & boundary", icon: Crosshair },
  { id: "methodology", title: "4. Methodology & Coverage", subtitle: "PTES / OWASP & life-cycle", icon: Layers },
  { id: "timeline", title: "5. Timeline & Deliverables", subtitle: "Fieldwork dates & reports", icon: Calendar },
  { id: "dependencies", title: "6. Assumptions & Deps", subtitle: "Preconditions & access", icon: CheckSquare },
  { id: "disclaimers", title: "7 & 8. Disclaimers & Exclusions", subtitle: "Risk notice & out of scope", icon: ShieldAlert },
  { id: "commercial", title: "9. Commercial Terms", subtitle: "Pricing, currency & legal", icon: DollarSign },
  { id: "appendix", title: "Appendix A. RoE", subtitle: "Operational rules & contacts", icon: Terminal },
  { id: "acceptance", title: "10. Acceptance & Billing", subtitle: "Corporate entities & signers", icon: UserCheck }
];

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addWorkingDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  let added = 0;
  while (added < days) {
    cur.setDate(cur.getDate() + 1);
    const dayOfWeek = cur.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return cur;
}

function addCalendarDays(startDate: Date, days: number): Date {
  const cur = new Date(startDate.getTime());
  cur.setDate(cur.getDate() + days);
  return cur;
}

function getNextBusinessMonday(): Date {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

export const SowEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  usePageTitle("Statement of Work Builder");
  const { username: currentUsername } = useUserCredentials();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any | null>(null);
  const [sowStatus, setSowStatus] = useState<SowStatus | null>(null);
  const [sowVersion, setSowVersion] = useState<number>(1);
  const [sowBlob, setSowBlob] = useState<SowBlob | null>(null);

  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<SectionTab>("targets");
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number>(0);
  const [lastSavedFieldsStr, setLastSavedFieldsStr] = useState<string>("");
  const [fields, setFields] = useState<SowFields>(() => emptySowFields());

  const isFormDirty = useMemo(() => {
    if (!lastSavedFieldsStr) return false;
    return JSON.stringify(fields) !== lastSavedFieldsStr;
  }, [fields, lastSavedFieldsStr]);

  const [isEditorLocked, setIsEditorLocked] = useState<boolean>(false);

  // Exclusive panel toggles: toggling one panel automatically switches to the other
  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      setShowPreview(!next);
      return next;
    });
  }, []);

  const handleTogglePreview = useCallback(() => {
    setShowPreview((prev) => {
      const next = !prev;
      setIsSidebarOpen(!next);
      return next;
    });
  }, []);

  // Version History viewing state
  const [showVersionModal, setShowVersionModal] = useState<boolean>(false);
  const [selectedVersionEntry, setSelectedVersionEntry] = useState<SowHistoryEntry | null>(null);

  // ── Extract modules from intake data ──────────────────────────────────────────
  const sowModules = useMemo<SowModule[]>(() => {
    if (!data) return [];
    const modules: SowModule[] = [];

    const rawAssets: any[] = Array.isArray(data.answers?.business_assets)
      ? data.answers.business_assets
      : Array.isArray(data.answers?.business_assets?.rawValue)
      ? data.answers.business_assets.rawValue
      : [];

    let targetBuilderAnswers: any[] = [];
    if (data.answers) {
      if (Array.isArray(data.answers.target_builder)) {
        targetBuilderAnswers = data.answers.target_builder;
      } else if (Array.isArray(data.answers.target_builder?.rawValue)) {
        targetBuilderAnswers = data.answers.target_builder.rawValue;
      } else if (typeof data.answers === "object") {
        for (const k of Object.keys(data.answers)) {
          if (k === "business_assets") continue;
          const entry = data.answers[k];
          const val = entry?.rawValue !== undefined ? entry.rawValue : entry;
          if (Array.isArray(val) && val.length > 0 && (val[0]?.assessment_scopes || val[0]?.asset_name || val[0]?.target_url || val[0]?.asset_url)) {
            targetBuilderAnswers = val;
            break;
          }
        }
      }
    }

    const candidateAssets = rawAssets.length > 0 ? rawAssets : targetBuilderAnswers;

    for (const asset of candidateAssets) {
      const rawName = asset.asset_name || asset.name || asset.title || "Asset";
      const cleanedName = rawName.replace(/\s*-\s*undefined\s*$/i, "").trim();

      const assetTargets: string[] = [];
      if (asset.target_url) assetTargets.push(asset.target_url);
      if (asset.asset_url) assetTargets.push(asset.asset_url);
      if (asset.target_value) assetTargets.push(asset.target_value);
      if (Array.isArray(asset.targets)) {
        asset.targets.forEach((t: any) => {
          const val = typeof t === "string" ? t : t?.target_value || t?.url || t?.name || t?.domain || t?.ip;
          if (val) assetTargets.push(val);
        });
      }

      const scopes = Array.isArray(asset.assessment_scopes) && asset.assessment_scopes.length > 0
        ? asset.assessment_scopes
        : [{ assessment_type: asset.assessment_type || asset.asset_type || "WEB_APP", access_type: asset.access_type, targets: assetTargets }];

      for (const scope of scopes) {
        const scopeTargets: string[] = [];
        let envStage = "Production";

        if (scope.target_url) scopeTargets.push(scope.target_url);
        if (scope.target_value) scopeTargets.push(scope.target_value);
        if (Array.isArray(scope.targets)) {
          scope.targets.forEach((t: any) => {
            const val = typeof t === "string" ? t : t?.target_value || t?.url || t?.name || t?.domain || t?.ip;
            if (val) scopeTargets.push(val);
            const envVal = t?.environment_stage || t?.environment;
            if (envVal) {
              const e = String(envVal).toUpperCase();
              if (e.includes("STAG")) envStage = "Staging";
              else if (e.includes("DEV")) envStage = "Development";
              else if (e.includes("UAT")) envStage = "UAT";
              else if (e.includes("PRE")) envStage = "Pre-prod";
              else envStage = "Production";
            }
          });
        }

        const combinedTargets = Array.from(new Set([...scopeTargets, ...assetTargets].filter(Boolean)));
        const rawType = String(scope.assessment_type || asset.assessment_type || asset.asset_type || "WEB_APP").toUpperCase();
        let normalizedType = "WEB_APP";
        if (rawType.includes("WEB")) normalizedType = "WEB_APP";
        else if (rawType.includes("API")) normalizedType = "API";
        else if (rawType.includes("MOBILE")) normalizedType = "MOBILE_APP";
        else if (rawType.includes("EXTERNAL")) normalizedType = "EXTERNAL_NETWORK";
        else if (rawType.includes("INTERNAL")) normalizedType = "INTERNAL_NETWORK";
        else if (rawType.includes("CLOUD_PENTEST") || rawType.includes("INFRASTRUCTURE")) normalizedType = "CLOUD_PENTEST";
        else if (rawType.includes("CLOUD")) normalizedType = "CLOUD_CONFIG";
        else if (rawType.includes("AI") || rawType.includes("LLM")) normalizedType = "AI_LLM";
        else if (rawType.includes("CODE")) normalizedType = "CODE_REVIEW";

        modules.push({
          assetName: cleanedName,
          assessmentType: normalizedType,
          isGraybox: !!(
            scope.access_type === "graybox" ||
            scope.isGraybox ||
            scope.has_credentials ||
            asset.access_type === "graybox" ||
            asset.isGraybox ||
            scope.testing_approach === "GREY_BOX" ||
            scope.testing_approach === "WHITE_BOX" ||
            scope.details?.login_required === true ||
            (Array.isArray(scope.targets?.[0]?.access_levels) && scope.targets[0].access_levels.length > 0)
          ),
          targets: combinedTargets.length > 0 ? combinedTargets : [cleanedName],
          environment: envStage,
          status: "Confirmed",
          description: `Assessment of ${cleanedName} across identified endpoints and business workflows.`
        });
      }
    }

    // Fallback to classified assets
    const classified = data.enrichment?.pricing?.standard?.classifiedAssets;
    if (modules.length === 0 && Array.isArray(classified) && classified.length > 0) {
      classified.forEach((ca: any) => {
        const rawName = ca.asset_name || "Asset";
        const cleanedName = rawName.replace(/\s*-\s*undefined\s*$/i, "").trim();
        const rawType = String(ca.methodology || "WEB_APP").toUpperCase();
        let normalizedType = "WEB_APP";
        if (rawType.includes("WEB")) normalizedType = "WEB_APP";
        else if (rawType.includes("API")) normalizedType = "API";
        else if (rawType.includes("MOBILE")) normalizedType = "MOBILE_APP";
        else if (rawType.includes("EXTERNAL")) normalizedType = "EXTERNAL_NETWORK";
        else if (rawType.includes("INTERNAL")) normalizedType = "INTERNAL_NETWORK";
        else if (rawType.includes("CLOUD")) normalizedType = "CLOUD_CONFIG";

        const envRaw = String(ca.environment || "PRODUCTION").toUpperCase();
        let envStage = "Production";
        if (envRaw.includes("STAG")) envStage = "Staging";
        else if (envRaw.includes("DEV")) envStage = "Development";

        modules.push({
          assetName: cleanedName,
          assessmentType: normalizedType,
          isGraybox: ca.testingApproach !== "BLACK_BOX",
          targets: Array.isArray(ca.targets) && ca.targets.length > 0 ? ca.targets.filter(Boolean) : [cleanedName],
          environment: envStage,
          status: "Confirmed",
          description: `Assessment of ${cleanedName} across identified endpoints.`
        });
      });
    }

    return modules;
  }, [data]);

  // ── Load scoping session details & SoW draft state ────────────────────────────
  useEffect(() => {
    if (!id) return;
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const [respData, sowData] = await Promise.all([
          getResponseById(id),
          getSow(id).catch(() => null)
        ]);

        if (!isMounted) return;
        setData(respData);

        const pricing = respData.enrichment?.pricing;
        const initialPrice = pricing?.manualOverridePrice ?? pricing?.standard?.adjustedPrice ?? null;
        const initialCurrency = (pricing?.standard?.currency as "NZD" | "USD") || "NZD";
        const initialDays = (pricing?.standard as any)?.scopeDays ?? (pricing?.standard as any)?.consultantDays ?? null;

        const clientName = respData.client_name || "Client";
        const clientEmail = respData.client_email || respData.respondent_email || respData.answers?.contact_email || respData.answers?.email || "";
        const clientPhone = respData.client_phone || respData.phone || respData.answers?.contact_phone || respData.answers?.phone || "";
        const clientSignerName = respData.nda_signer_name || respData.client_name || respData.answers?.contact_name || "";
        const clientSignerTitle = respData.answers?.client_role || respData.answers?.contact_title || "";
        const clientRegisteredCompany = respData.company_name || respData.client_name || "";
        const clientCountryOfIncorporation = respData.country || "New Zealand";
        const clientBillingName = respData.answers?.billing_name || respData.client_name || "";
        const clientBillingEmail = respData.answers?.billing_email || clientEmail || "";
        const clientBillingPhone = respData.answers?.billing_phone || clientPhone || "";
        const clientBillingAddress = respData.answers?.billing_address || "";
        const clientTaxNumber = respData.answers?.tax_number || respData.answers?.gst_number || "";
        const clientEmergencyContact = respData.emergency_contact || respData.answers?.emergency_contact || clientPhone || "";
        const preferredStartDate = respData.preferred_start_date || respData.answers?.preferred_start_date || respData.answers?.target_start_date || "";

        // Build base default fields with smart logical mappings
        const def = emptySowFields(clientName);
        def.price = initialPrice;
        def.currency = initialCurrency;
        def.consultantDays = initialDays;

        def.introductionText = `This Statement of Work (“SOW”) is entered into by and between Central Test Bed Limited (“CTB”) and ${clientName} (“Client”), and defines the scope of work, technical objectives, and terms under which CTB will deliver professional security assessment and penetration testing services.`;
        def.objectiveText = `The primary objective of this engagement is to assess the security posture of ${clientName}’s designated target environments, identify vulnerabilities, validate exploitable attack vectors, evaluate business impact, and deliver actionable remediation guidance to strengthen ${clientName}’s defensive baseline.`;
        def.scopeBoundaryText = `Testing is strictly limited to the targets and assets explicitly listed in Table 1 above. Any third-party infrastructure, external service providers, downstream payment processors, or unlisted domains and IP addresses are strictly out of scope unless explicitly authorised in writing.`;

        // Smart Timeline
        const validStart = preferredStartDate ? new Date(preferredStartDate) : getNextBusinessMonday();
        const days = initialDays && initialDays > 0 ? initialDays : 5;
        def.testingStartDate = toIso(validStart);
        const fieldworkEnd = addWorkingDays(validStart, Math.max(1, days - 1));
        def.testingEndDate = toIso(fieldworkEnd);
        const draftReport = addWorkingDays(fieldworkEnd, 2);
        def.draftReportDate = toIso(draftReport);
        const finalReport = addWorkingDays(draftReport, 5);
        def.finalReportDate = toIso(finalReport);
        const retestEnd = addCalendarDays(finalReport, 30);
        def.retestingEndDate = toIso(retestEnd);
        def.indicativeDeliveryDuration = `${days} Business Days (Fieldwork)`;
        def.baselineAssessmentDuration = `${days} Days`;
        def.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;

        // Contact and billing
        if (clientSignerName) def.clientSignerName = clientSignerName;
        if (clientSignerTitle) def.clientSignerTitle = clientSignerTitle;
        if (clientEmail) def.clientEmail = clientEmail;
        if (clientPhone) def.clientPhone = clientPhone;
        if (clientRegisteredCompany) def.clientRegisteredCompany = clientRegisteredCompany;
        if (clientCountryOfIncorporation) def.clientCountryOfIncorporation = clientCountryOfIncorporation;
        if (clientBillingName) def.clientBillingName = clientBillingName;
        if (clientBillingEmail) def.clientBillingEmail = clientBillingEmail;
        if (clientBillingPhone) def.clientBillingPhone = clientBillingPhone;
        if (clientBillingAddress) def.clientBillingAddress = clientBillingAddress;
        if (clientTaxNumber) def.clientTaxNumber = clientTaxNumber;
        if (clientEmergencyContact) def.clientEmergencyContact = clientEmergencyContact;

        def.ctbEngagementLead = currentUsername ? `${currentUsername} (Cybersecurity Solutions Specialist)` : "Ankita Dhakar (Cybersecurity Solutions Specialist)";
        def.ctbSignerName = currentUsername || "Ankita Dhakar";
        def.ctbSignerTitle = "Head of Cybersecurity / Solutions Specialist";

        if (sowData) {
          setSowStatus(sowData.sow_status);
          setSowVersion(sowData.sow_version || 1);
          setSowBlob(sowData.sow_data);
          if (sowData.sow_status === "IN_REVIEW" || sowData.sow_status === "FINAL") {
            setIsEditorLocked(true);
          }

          if (sowData.sow_data?.fields) {
            const saved = sowData.sow_data.fields;
            const merged: any = { ...def, ...saved };
            (Object.keys(def) as (keyof SowFields)[]).forEach((key) => {
              const val = saved[key];
              if (val === undefined || val === null) {
                merged[key] = def[key];
              }
            });
            setFields(merged);
            setLastSavedFieldsStr(JSON.stringify(merged));
            return;
          }
        }

        setFields(def);
        setLastSavedFieldsStr(JSON.stringify(def));
      } catch (err) {
        console.error("Failed to load scoping session / SoW state:", err);
        toast.error("Failed to load scoping session details.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [id, currentUsername]);

  // Auto-sync targets when sowModules are calculated if targetItems is empty
  useEffect(() => {
    if (sowModules.length > 0 && fields.targetItems.length === 0) {
      const generated = sowModules.map((mod, idx) => ({
        id: `tgt-${idx + 1}`,
        assetName: mod.assetName || "",
        assessmentType: mod.assessmentType || "WEB_APP",
        accessType: (mod.isGraybox ? "graybox" : "blackbox") as any,
        targets: Array.isArray(mod.targets) ? mod.targets.join(", ") : (mod.targets || ""),
        environment: mod.environment || "Production",
        status: mod.status || "Confirmed",
        description: mod.description || `Assessment of ${mod.assetName || "the target"} across identified endpoints.`
      }));
      setFields((prev) => ({
        ...prev,
        targetItems: generated,
        authorisedTargets: generated.map((t) => `• ${t.assetName} (${t.assessmentType}, ${t.accessType.toUpperCase()}): ${t.targets} [${t.environment}]`).join("\n"),
        testingEnvironment: generated[0]?.environment || prev.testingEnvironment
      }));
    }
  }, [sowModules, fields.targetItems.length]);

  const setField = useCallback(<K extends keyof SowFields>(key: K, value: SowFields[K]) => {
    setFields((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "consultantDays") {
        const days = typeof value === "number" && value > 0 ? value : null;
        if (days !== null) {
          next.indicativeDeliveryDuration = `${days} Business Days (Fieldwork)`;
          next.baselineAssessmentDuration = `${days} ${days === 1 ? "Day" : "Days"}`;

          if (prev.testingStartDate) {
            const start = new Date(prev.testingStartDate);
            if (!isNaN(start.getTime())) {
              const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
              const draftReport = addWorkingDays(fieldworkEnd, 2);
              const finalReport = addWorkingDays(draftReport, 5);
              const retestEnd = addCalendarDays(finalReport, 30);

              next.testingEndDate = toIso(fieldworkEnd);
              next.draftReportDate = toIso(draftReport);
              next.finalReportDate = toIso(finalReport);
              next.retestingEndDate = toIso(retestEnd);
            }
          }

          if (prev.timelineAssumptionsText) {
            next.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;
          }
        }
      } else if (key === "indicativeDeliveryDuration") {
        if (typeof value === "string" && value) {
          const match = value.match(/^(\d+(?:\.\d+)?)/);
          if (match) {
            const days = parseFloat(match[1]);
            if (!isNaN(days) && days > 0) {
              next.consultantDays = days;
              next.baselineAssessmentDuration = `${days} ${days === 1 ? "Day" : "Days"}`;

              if (prev.testingStartDate) {
                const start = new Date(prev.testingStartDate);
                if (!isNaN(start.getTime())) {
                  const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
                  const draftReport = addWorkingDays(fieldworkEnd, 2);
                  const finalReport = addWorkingDays(draftReport, 5);
                  const retestEnd = addCalendarDays(finalReport, 30);

                  next.testingEndDate = toIso(fieldworkEnd);
                  next.draftReportDate = toIso(draftReport);
                  next.finalReportDate = toIso(finalReport);
                  next.retestingEndDate = toIso(retestEnd);
                }
              }

              if (prev.timelineAssumptionsText) {
                next.timelineAssumptionsText = `Fieldwork is estimated at ${days} business days based on the agreed scoping asset inventory. Retesting of remediated findings is available for up to 90 days following completion of fieldwork.`;
              }
            }
          }
        }
      } else if (key === "testingStartDate") {
        if (typeof value === "string" && value) {
          const start = new Date(value);
          if (!isNaN(start.getTime())) {
            const days = prev.consultantDays && prev.consultantDays > 0 ? prev.consultantDays : 5;
            const fieldworkEnd = addWorkingDays(start, Math.max(1, days - 1));
            const draftReport = addWorkingDays(fieldworkEnd, 2);
            const finalReport = addWorkingDays(draftReport, 5);
            const retestEnd = addCalendarDays(finalReport, 30);

            next.testingEndDate = toIso(fieldworkEnd);
            next.draftReportDate = toIso(draftReport);
            next.finalReportDate = toIso(finalReport);
            next.retestingEndDate = toIso(retestEnd);
          }
        }
      }

      return next;
    });
  }, []);

  // ── Action Handlers ─────────────────────────────────────────────────────────
  const handleSaveDraft = async () => {
    if (!id) return;
    try {
      setSaving(true);
      const res = await saveSowDraft(id, fields);
      setSowStatus(res.sow_status);
      setSowVersion(res.sow_version);
      setSowBlob(res.sow_data);
      setLastSavedAt(Date.now());
      setLastSavedFieldsStr(JSON.stringify(fields));
      toast.success("SoW Draft saved successfully");
    } catch (err: any) {
      console.error("Failed to save draft:", err);
      toast.error(err?.response?.data?.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!id) return;
    try {
      setSubmitting(true);
      // Auto-save fields first
      await saveSowDraft(id, fields);
      const res = await submitSowForReview(id);
      setSowStatus(res.sow_status);
      setLastSavedFieldsStr(JSON.stringify(fields));
      setIsEditorLocked(true);
      toast.success("Statement of Work submitted for review.");
    } catch (err: any) {
      console.error("Submit review failed:", err);
      toast.error(err?.response?.data?.message || "Failed to submit for review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      setApproving(true);
      const res = await approveSow(id);
      setSowStatus(res.sow_status);
      setIsEditorLocked(true);
      toast.success("Statement of Work approved and finalised.");
    } catch (err: any) {
      console.error("Approval failed:", err);
      toast.error(err?.response?.data?.message || "Failed to approve SoW");
    } finally {
      setApproving(false);
    }
  };

  const handleReopen = async () => {
    if (!id) return;
    try {
      setReopening(true);
      const res = await reopenSow(id);
      setSowStatus(res.sow_status);
      setIsEditorLocked(false);
      if (res.sow_version) {
        setSowVersion(res.sow_version);
        setFields((prev) => ({
          ...prev,
          coverVersionLabel: `${res.sow_version}.0`
        }));
      }
      if (res.sow_data) {
        setSowBlob(res.sow_data);
      }
      toast.success(
        `Statement of Work reopened for editing (v${res.sow_version || sowVersion + 1}).`
      );
    } catch (err: any) {
      console.error("Reopen failed:", err);
      toast.error(err?.response?.data?.message || "Failed to reopen SoW");
    } finally {
      setReopening(false);
    }
  };

  const clientName = data?.client_name || "Client";
  const isFinal = sowStatus === "FINAL";
  const isReview = sowStatus === "IN_REVIEW";

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50 gap-3 text-slate-500">
        <Loader2 size={32} className="animate-spin text-ctb-blue" />
        <span className="text-sm font-semibold">Loading Statement of Work Builder…</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-100 overflow-hidden font-sans">
      {/* ─── Top Master Header ─── */}
      <header className="h-14 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 z-20 shadow-xs">
        <div className="flex items-center gap-3">
          <Link
            to={`/dashboard/sales/scoping/${id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition"
          >
            <ArrowLeft size={14} />
            Back to Scoping
          </Link>

          <div className="h-4 w-[1px] bg-slate-200" />

          <div className="flex items-center gap-2">
            <h1 className="text-sm font-bold text-slate-900">
              SOW Builder: <span className="text-ctb-blue">{clientName}</span>
            </h1>

            {/* Status badge */}
            <span
              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${
                sowStatus === "FINAL"
                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : sowStatus === "IN_REVIEW"
                  ? "bg-blue-50 text-blue-800 border-blue-200"
                  : "bg-amber-50 text-amber-800 border-amber-200"
              }`}
            >
              {sowStatus || "DRAFT"}
            </span>

            {selectedVersionEntry ? (
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                <Clock size={11} className="text-amber-700" />
                v{selectedVersionEntry.version} (Viewing Old Version)
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                v{sowVersion}
              </span>
            )}

            {/* Version History Button */}
            <button
              type="button"
              onClick={() => setShowVersionModal(true)}
              className="px-2.5 py-1 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1.5 transition cursor-pointer border border-slate-200"
              title="View past versions and draft copies"
            >
              <History size={13} className="text-ctb-blue" />
              <span>Version History</span>
              {sowBlob?.history && sowBlob.history.length > 0 && (
                <span className="px-1.5 py-0.2 bg-blue-100 text-ctb-blue rounded-full text-[10px] font-bold">
                  {sowBlob.history.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {!selectedVersionEntry && (
            <>
              {/* Toggle Sections Sidebar */}
              <button
                type="button"
                onClick={handleToggleSidebar}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                  isSidebarOpen
                    ? "bg-blue-50 text-ctb-blue border border-blue-200"
                    : "text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200"
                }`}
                title={isSidebarOpen ? "Collapse Document Sections sidebar" : "Expand Document Sections sidebar"}
              >
                <Layers size={13} />
                {isSidebarOpen ? "Hide Sections" : "Show Sections"}
              </button>

              {/* Toggle PDF preview */}
              <button
                type="button"
                onClick={handleTogglePreview}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition cursor-pointer ${
                  showPreview
                    ? "bg-blue-50 text-ctb-blue border border-blue-200"
                    : "text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200"
                }`}
              >
                <Columns size={13} />
                {showPreview ? "Hide Preview" : "Show PDF Preview"}
              </button>

              {/* Unlock for Edit Button when editor is locked (and not FINAL) */}
              {isEditorLocked && !isFinal && (
                <button
                  type="button"
                  onClick={() => setIsEditorLocked(false)}
                  className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer shadow-xs transition"
                  title="Unlock editor to make changes"
                >
                  <Unlock size={13} />
                  Unlock for Edit
                </button>
              )}

              {/* Save Changes button pops up ONLY when there are unsaved changes */}
              {isFormDirty && (
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer shadow-sm transition animate-pulse"
                  title="Unsaved changes detected — click to save draft"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Save Changes
                </button>
              )}

              {/* Submit for Review / Approval buttons */}
              {!isFinal && !isReview && (
                <button
                  type="button"
                  onClick={handleSubmitReview}
                  disabled={submitting}
                  className="px-4 py-1.5 bg-ctb-blue hover:bg-ctb-blue/90 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer shadow-sm transition disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <FileCheck size={12} />}
                  Submit for Review
                </button>
              )}

              {isReview && (
                <>
                  <button
                    type="button"
                    onClick={handleReopen}
                    disabled={reopening}
                    className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer transition disabled:opacity-50"
                  >
                    {reopening ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Reopen
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={approving}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer shadow-sm transition disabled:opacity-50"
                  >
                    {approving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                    Approve & Finalise
                  </button>
                </>
              )}

              {isFinal && (
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={reopening}
                  className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 cursor-pointer shadow-sm transition disabled:opacity-50"
                >
                  {reopening ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                  Reopen for Editing (v{sowVersion + 1}.0)
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* ─── Old Version Banner Notification ─── */}
      {selectedVersionEntry && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center justify-between shrink-0 shadow-xs z-10">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
              <Clock size={16} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-amber-900">
                  You are viewing Version {selectedVersionEntry.version} (Read-Only)
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-200 text-amber-900 uppercase tracking-wide">
                  Old Version Snapshot
                </span>
              </div>
              <p className="text-[11px] text-amber-800 mt-0.5">
                Saved on {new Date(selectedVersionEntry.savedAt).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSelectedVersionEntry(null)}
            className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg text-xs transition cursor-pointer shadow-xs flex items-center gap-1.5 shrink-0"
          >
            <RotateCcw size={13} />
            Return to Active Version (v{sowVersion})
          </button>
        </div>
      )}

      {/* ─── Main Content Workspace ─── */}
      <div className="flex-1 flex overflow-hidden">
        {selectedVersionEntry ? (
          /* Viewing Old Version: Hide editor & sidebar, show PDF preview in middle */
          <main className="flex-1 h-full bg-slate-100 p-4 md:p-6 flex flex-col items-center justify-center overflow-y-auto">
            <div className="w-full h-full max-w-5xl bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden flex flex-col">
              <SowPreviewPanel
                fields={selectedVersionEntry.fields}
                clientName={clientName}
                sowVersion={selectedVersionEntry.version}
                modules={sowModules}
              />
            </div>
          </main>
        ) : (
          /* Active Version Layout: Sidebar + Editor + Live PDF Preview */
          <>
            {/* Left Section Navigation Sidebar (Expanded or Collapsed Icon Bar) */}
            {isSidebarOpen ? (
              <aside className="w-72 bg-white border-r border-slate-200 flex flex-col shrink-0 transition-all duration-200">
                <div className="p-3.5 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers size={15} className="text-ctb-blue" />
                    <span className="text-xs font-bold text-slate-800">Document Sections</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleSidebar}
                    className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-md transition cursor-pointer"
                    title="Collapse to icon sidebar"
                  >
                    <ChevronLeft size={16} />
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto p-2 space-y-1">
                  {SECTIONS.map((sec) => {
                    const Icon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => setActiveSection(sec.id)}
                        className={`w-full text-left p-2.5 rounded-xl flex items-start gap-3 transition cursor-pointer ${
                          isActive
                            ? "bg-ctb-blue text-white shadow-xs font-semibold"
                            : "hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <Icon size={16} className={`mt-0.5 shrink-0 ${isActive ? "text-white" : "text-slate-400"}`} />
                        <div className="min-w-0">
                          <div className="text-xs font-medium leading-tight truncate">{sec.title}</div>
                          <div className={`text-[10px] truncate mt-0.5 ${isActive ? "text-blue-100" : "text-slate-400"}`}>
                            {sec.subtitle}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </nav>

                <div className="p-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
                  <p className="font-semibold text-slate-700">SOW Status: <span className="uppercase text-ctb-blue font-bold">{sowStatus || "DRAFT"}</span></p>
                  <p className="mt-0.5 text-[10px]">Version: v{sowVersion}.0</p>
                </div>
              </aside>
            ) : (
              /* Collapsed Icon-Only Left Sidebar */
              <aside className="w-16 bg-white border-r border-slate-200 flex flex-col items-center shrink-0 py-3 transition-all duration-200">
                <button
                  type="button"
                  onClick={handleToggleSidebar}
                  className="p-2 text-slate-500 hover:text-ctb-blue hover:bg-blue-50 rounded-xl transition cursor-pointer mb-2"
                  title="Expand section titles (Hide PDF preview)"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="w-8 h-px bg-slate-200 mb-2" />
                <nav className="flex-1 w-full overflow-y-auto space-y-1 px-2 flex flex-col items-center">
                  {SECTIONS.map((sec) => {
                    const Icon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => setActiveSection(sec.id)}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition cursor-pointer relative group ${
                          isActive
                            ? "bg-ctb-blue text-white shadow-xs"
                            : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                        title={sec.title}
                      >
                        <Icon size={17} />
                        <div className="absolute left-full ml-2 px-2.5 py-1 bg-slate-900 text-white text-[11px] font-semibold rounded-lg shadow-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-30">
                          {sec.title}
                        </div>
                      </button>
                    );
                  })}
                </nav>
                <div className="mt-auto pt-2 border-t border-slate-200 w-full flex flex-col items-center">
                  <span className="text-[10px] font-bold text-slate-400">v{sowVersion}</span>
                </div>
              </aside>
            )}

            {/* Center Editor Workspace (Clean spacious layout) */}
            <main className="flex-1 min-w-[560px] overflow-y-auto bg-slate-50/50 p-4 md:p-6">
              {(isEditorLocked || isFinal) && (
                <div className="max-w-4xl mx-auto mb-4 p-3.5 bg-slate-900 text-white rounded-xl flex items-center justify-between shadow-xs border border-slate-800">
                  <div className="flex items-center gap-2 text-xs">
                    <Lock size={15} className="text-amber-400 shrink-0" />
                    <span className="font-semibold">
                      {isFinal
                        ? `Statement of Work is Finalised & Approved (v${sowVersion}.0). Edits are permanently locked.`
                        : `Editor is locked (${sowStatus === "IN_REVIEW" ? "Submitted for Review" : sowStatus || "Submitted"}). Edits are currently disabled.`}
                    </span>
                  </div>
                  {!isFinal ? (
                    <button
                      type="button"
                      onClick={() => setIsEditorLocked(false)}
                      className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-lg flex items-center gap-1.5 transition cursor-pointer shrink-0"
                    >
                      <Unlock size={12} />
                      Unlock for Edit
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleReopen}
                      disabled={reopening}
                      className="px-3.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs font-bold rounded-lg flex items-center gap-1.5 transition cursor-pointer shrink-0 shadow-sm"
                    >
                      {reopening ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                      Reopen for Editing (v{sowVersion + 1}.0)
                    </button>
                  )}
                </div>
              )}

              <div className={`w-full max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 transition-all ${(isEditorLocked || isFinal) ? "pointer-events-none opacity-75 select-none" : ""}`}>
                {activeSection === "cover" && (
                  <CoverMetaEditor
                    fields={fields}
                    setField={setField}
                    clientName={clientName}
                    sowVersion={sowVersion}
                  />
                )}

                {activeSection === "intro" && (
                  <IntroObjectivesEditor
                    fields={fields}
                    setField={setField}
                    clientName={clientName}
                  />
                )}

                {activeSection === "targets" && (
                  <TargetsScopeEditor
                    fields={fields}
                    setField={setField}
                    clientName={clientName}
                  />
                )}

                {activeSection === "methodology" && (
                  <MethodologyCoverageEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "timeline" && (
                  <TimelineDeliverablesEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "dependencies" && (
                  <DependenciesAssumptionsEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "disclaimers" && (
                  <DisclaimersExclusionsEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "commercial" && (
                  <CommercialTermsEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "appendix" && (
                  <AppendixARulesOfEngagementEditor
                    fields={fields}
                    setField={setField}
                  />
                )}

                {activeSection === "acceptance" && (
                  <AcceptanceSignaturesEditor
                    fields={fields}
                    setField={setField}
                    clientName={clientName}
                  />
                )}

                {/* Bottom Section Pager */}
                <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-between">
                  {(() => {
                    const currentIdx = SECTIONS.findIndex((s) => s.id === activeSection);
                    const prevSec = currentIdx > 0 ? SECTIONS[currentIdx - 1] : null;
                    const nextSec = currentIdx < SECTIONS.length - 1 ? SECTIONS[currentIdx + 1] : null;
                    return (
                      <>
                        {prevSec ? (
                          <button
                            type="button"
                            onClick={() => setActiveSection(prevSec.id)}
                            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition cursor-pointer"
                          >
                            ← Previous: {prevSec.title}
                          </button>
                        ) : <div />}

                        {nextSec ? (
                          <button
                            type="button"
                            onClick={() => setActiveSection(nextSec.id)}
                            className="px-4 py-1.5 text-xs font-bold text-white bg-ctb-blue hover:bg-ctb-blue/90 rounded-lg shadow-sm transition cursor-pointer"
                          >
                            Next: {nextSec.title} →
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSaveDraft}
                            className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm transition cursor-pointer"
                          >
                            Save Final Draft
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </main>

            {/* Right Live PDF Preview Panel */}
            {showPreview && (
              <aside className="w-[45%] lg:w-[48%] xl:w-[50%] shrink-0 h-full border-l border-slate-200">
                <SowPreviewPanel
                  fields={fields}
                  clientName={clientName}
                  sowVersion={sowVersion}
                  modules={sowModules}
                  lastSavedAt={lastSavedAt}
                />
              </aside>
            )}
          </>
        )}
      </div>

      {/* ─── Version History Modal ─── */}
      {showVersionModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-3xl flex flex-col max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-blue-50 text-ctb-blue rounded-xl border border-blue-100">
                  <History size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900">SOW Draft Version History</h3>
                  <p className="text-xs text-slate-500">
                    View all past saved draft copies for <span className="font-semibold text-slate-700">{clientName}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowVersionModal(false);
                  setSelectedVersionEntry(null);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-4">
              {/* Banner when viewing an old version */}
              {selectedVersionEntry && (
                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs text-amber-900">
                  <div className="flex items-center gap-2">
                    <Clock size={15} className="text-amber-600 shrink-0" />
                    <span>
                      Currently previewing <strong className="font-bold">Version {selectedVersionEntry.version}</strong> (saved {new Date(selectedVersionEntry.savedAt).toLocaleString()})
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedVersionEntry(null)}
                    className="px-2.5 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-lg transition text-[11px] cursor-pointer"
                  >
                    Return to Active Version (v{sowVersion})
                  </button>
                </div>
              )}

              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Saved Draft Versions ({sowBlob?.history?.length || 0})
              </div>

              {!sowBlob?.history || sowBlob.history.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200 space-y-1">
                  <p className="text-xs font-semibold text-slate-700">No previous draft snapshots saved yet.</p>
                  <p className="text-[11px] text-slate-400">
                    Clicking "Save Draft" will automatically create and store a version snapshot in this list.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {/* Current Active Version Card */}
                  <div className="p-4 rounded-xl border-2 border-ctb-blue/30 bg-blue-50/40 flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-ctb-blue text-white">
                          v{sowVersion} (Current Active)
                        </span>
                        <span className="text-xs font-bold text-slate-800">Latest Working Copy</span>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Active draft currently loaded in the editor.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedVersionEntry(null);
                        setShowVersionModal(false);
                      }}
                      className="px-3 py-1.5 bg-ctb-blue hover:bg-ctb-blue/90 text-white font-bold rounded-lg text-xs transition cursor-pointer shadow-xs"
                    >
                      Active Version
                    </button>
                  </div>

                  {/* Past History List */}
                  {sowBlob.history
                    .slice()
                    .reverse()
                    .map((entry) => {
                      const isSelected = selectedVersionEntry?.version === entry.version;
                      return (
                        <div
                          key={entry.version}
                          className={`p-4 rounded-xl border transition flex items-center justify-between ${
                            isSelected
                              ? "border-amber-400 bg-amber-50/50 shadow-xs"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                Version {entry.version}
                              </span>
                              <span className="text-xs font-semibold text-slate-800">
                                {entry.savedByUsername ? `Saved by ${entry.savedByUsername}` : "Saved Draft Copy"}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-[11px] text-slate-400">
                              <span>Saved on: {new Date(entry.savedAt).toLocaleString()}</span>
                              <span>•</span>
                              <span>Target count: {entry.fields?.targetItems?.length || 0}</span>
                              <span>•</span>
                              <span>Currency: {entry.fields?.currency || "NZD"} ${entry.fields?.price ? entry.fields.price.toLocaleString() : "0"}</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedVersionEntry(entry);
                              setShowVersionModal(false);
                            }}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                              isSelected
                                ? "bg-amber-600 text-white"
                                : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
                            }`}
                          >
                            <Eye size={13} />
                            {isSelected ? "Viewing in Preview" : `View Version ${entry.version}`}
                          </button>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
              <span>Selecting a past version displays its exact PDF copy in the Live Preview panel without overwriting your current active draft.</span>
              <button
                type="button"
                onClick={() => {
                  setShowVersionModal(false);
                  setSelectedVersionEntry(null);
                }}
                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-lg transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SowEditorPage;
